/**
 * DrawerComponent
 *
 * Core component responsible for rendering a sliding side-panel drawer inside a modal dialog.
 * Supports dynamic content injection, header/footer slots, and smooth entry/exit animations controlled through signals.
 */

import {
  Component,
  Type,
  signal,
  Inject,
  ViewContainerRef,
  ComponentRef,
  AfterViewInit,
  viewChild,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { DrawerHeaderComponent } from './components/drawer-header/drawer-header.component';
import { DrawerFooterComponent } from './components/drawer-footer/drawer-footer.component';
import {
  DrawerComponentInputs,
  DrawerResult,
  DrawerState,
} from './drawer.model';
import { DrawerAnimationWrapperComponent } from './drawer-animation-wrapper.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'da-drawer',
  standalone: true,
  imports: [
    DrawerAnimationWrapperComponent,
    DrawerHeaderComponent,
    DrawerFooterComponent,
  ],
  templateUrl: './drawer.component.html',
  host: {
    /** Handle escape key to initiate drawer close */
    '(document:keydown.escape)': 'onEscapeKey($event)',
  },
})
export class DrawerComponent implements AfterViewInit {
  /** Target where dynamic content is created */
  public contentHost = viewChild('contentHost', { read: ViewContainerRef });

  /** Wrapper controlling entry/exit animations */
  public drawerWrapper =
    viewChild<DrawerAnimationWrapperComponent>('drawerWrapper');

  /** Reference to the created dynamic content component */
  public contentComponentRef: ComponentRef<unknown>;

  /** Dynamic component to load into drawer body */
  public contentComponent = signal<Type<unknown>>(undefined);

  /** Drawer state signal (title, buttons, flags) */
  public drawerState = signal<DrawerState>(undefined);

  /** Inputs to inject into the dynamic content component */
  public inputs = signal<Record<string, unknown>>({});

  /** Result to emit after drawer animation completes */
  private resultToReturn: DrawerResult;

  constructor(
    @Inject(DIALOG_DATA) public data: DrawerComponentInputs,
    private dialogRef: DialogRef
  ) {
    // Subscribe to backdrop click events to close the drawer if not disabled
    this.dialogRef.backdropClick.subscribe(() => {
      if (this.drawerState().disableClose) {
        return;
      }
      this.drawerClose({ result: 'close' });
    });

    // Initialize signals immediately from provided dialog data
    this.drawerState.set(this.data.drawer);
    this.contentComponent.set(this.data.contentComponent);
    this.inputs.set(this.data.inputs ?? {});
  }

  /** After view init, dynamically create the drawer content component */
  public ngAfterViewInit(): void {
    if (!this.contentComponent()) {
      return;
    }

    const componentRef = this.contentHost().createComponent(
      this.contentComponent()
    );

    for (const [key, value] of Object.entries(this.inputs())) {
      componentRef.setInput(key, value);
    }

    this.contentComponentRef = componentRef;
  }

  /** Start drawer close animation and prepare result */
  public drawerClose(result: DrawerResult) {
    this.resultToReturn = result;
    this.drawerWrapper().startAnimation();
  }

  /** Finalize and close the dialog after the exit animation completes */
  public drawerAnimationDone() {
    this.dialogRef.close(this.resultToReturn);
  }

  /** close drawer handler when escape key is pressed  */
  public onEscapeKey(event: KeyboardEvent) {
    const target = event.target as HTMLElement;

    if (this.drawerState().disableClose) {
      return;
    }

    if (target.closest('[cdkMenu]')) {
      return;
    }

    this.drawerClose({ result: 'close' });
  }
}
